/**
 * EPD image processing (aligned with 4ct-miniapp src/lib/epd.js).
 * Pure data — no DOM. Used by manager_web for preview + same semantics as server.
 */

export const EPD_WIDTH = 200
export const EPD_HEIGHT = 200

const PALETTE_RGB = [
  [0, 0, 0],
  [255, 255, 255],
  [255, 215, 0],
  [255, 0, 0],
]

export const PALETTE_RGBA = [
  [0, 0, 0, 255],
  [255, 255, 255, 255],
  [255, 215, 0, 255],
  [255, 0, 0, 255],
]

function nearestEpd(r, g, b) {
  let best = 0
  let bestD = Infinity
  for (let i = 0; i < 4; i++) {
    const [pr, pg, pb] = PALETTE_RGB[i]
    const d = (r - pr) ** 2 + (g - pg) ** 2 + (b - pb) ** 2
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  return best
}

function rgbToHsv(r255, g255, b255) {
  const r = r255 / 255
  const g = g255 / 255
  const b = b255 / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  if (d > 0) {
    if (max === r) h = ((g - b) / d + 6) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h /= 6
  }
  return [h, max > 0 ? d / max : 0, max]
}

function hsvToRgb(h, s, v) {
  const i = Math.floor(h * 6)
  const f = h * 6 - i
  const p = v * (1 - s)
  const q = v * (1 - f * s)
  const t = v * (1 - (1 - f) * s)
  let r
  let g
  let b
  switch (i % 6) {
    case 0:
      r = v
      g = t
      b = p
      break
    case 1:
      r = q
      g = v
      b = p
      break
    case 2:
      r = p
      g = v
      b = t
      break
    case 3:
      r = p
      g = q
      b = v
      break
    case 4:
      r = t
      g = p
      b = v
      break
    case 5:
      r = v
      g = p
      b = q
      break
    default:
      r = g = b = 0
  }
  return [r * 255, g * 255, b * 255]
}

function nearestEpdHsv(r, g, b) {
  const [h1, s1, v1] = rgbToHsv(r, g, b)
  let best = 0
  let bestD = Infinity
  for (let i = 0; i < 4; i++) {
    const [pr, pg, pb] = PALETTE_RGB[i]
    const [h2, s2, v2] = rgbToHsv(pr, pg, pb)
    let dh = Math.abs(h1 - h2)
    if (dh > 0.5) dh = 1 - dh
    const d = (dh * 2) ** 2 + ((s1 - s2) * 1.5) ** 2 + (v1 - v2) ** 2
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  return best
}

function applyFilters(R, G, B, n, saturate, contrast, hue) {
  if (Math.abs(contrast - 1.0) > 0.001) {
    for (let i = 0; i < n; i++) {
      R[i] = 128 + (R[i] - 128) * contrast
      G[i] = 128 + (G[i] - 128) * contrast
      B[i] = 128 + (B[i] - 128) * contrast
    }
  }
  if (Math.abs(saturate - 1.0) > 0.001 || Math.abs(hue) > 0.5) {
    const hueShift = hue / 360
    for (let i = 0; i < n; i++) {
      const rc = Math.max(0, Math.min(255, R[i]))
      const gc = Math.max(0, Math.min(255, G[i]))
      const bc = Math.max(0, Math.min(255, B[i]))
      const [h, s, v] = rgbToHsv(rc, gc, bc)
      const newH = (h + hueShift + 10) % 1
      const newS = Math.min(1, Math.max(0, s * saturate))
      const [r2, g2, b2] = hsvToRgb(newH, newS, v)
      R[i] = r2
      G[i] = g2
      B[i] = b2
    }
  }
}

export function packbitsEncode(input) {
  const out = []
  let i = 0
  while (i < input.length) {
    let run = 1
    while (i + run < input.length && input[i + run] === input[i] && run < 129) run++
    if (run >= 2) {
      out.push(((run - 2) & 0x7f) | 0x80)
      out.push(input[i])
      i += run
      continue
    }
    const litStart = i
    let litEnd = i + 1
    while (litEnd < input.length && litEnd - litStart < 128) {
      if (litEnd + 1 < input.length && input[litEnd] === input[litEnd + 1]) break
      litEnd++
    }
    out.push(litEnd - litStart - 1)
    for (let p = litStart; p < litEnd; p++) out.push(input[p])
    i = litEnd
  }
  return new Uint8Array(out)
}

export function packbitsDecode(input) {
  const out = []
  let i = 0
  while (i < input.length) {
    const ctrl = input[i++]
    if (ctrl & 0x80) {
      const value = input[i++]
      const count = (ctrl & 0x7f) + 2
      for (let j = 0; j < count; j++) out.push(value)
    } else {
      const count = ctrl + 1
      for (let j = 0; j < count; j++) out.push(input[i++])
    }
  }
  return new Uint8Array(out)
}

export function processImageData(imageData, mode = 'rgb', saturate = 1.0, contrast = 1.0, hue = 0.0) {
  const { data, width, height } = imageData
  const n = width * height

  const R = new Float32Array(n)
  const G = new Float32Array(n)
  const B = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    R[i] = data[i * 4]
    G[i] = data[i * 4 + 1]
    B[i] = data[i * 4 + 2]
  }

  applyFilters(R, G, B, n, saturate, contrast, hue)

  const quant = new Uint8Array(n)

  if (mode === 'bw') {
    for (let i = 0; i < n; i++) {
      const luma =
        0.299 * Math.max(0, Math.min(255, R[i])) +
        0.587 * Math.max(0, Math.min(255, G[i])) +
        0.114 * Math.max(0, Math.min(255, B[i]))
      quant[i] = luma >= 128 ? 1 : 0
    }
  } else if (mode === 'eink') {
    for (let i = 0; i < n; i++) {
      quant[i] = nearestEpd(
        Math.max(0, Math.min(255, R[i])),
        Math.max(0, Math.min(255, G[i])),
        Math.max(0, Math.min(255, B[i])),
      )
    }
  } else {
    const nearest = mode === 'hsv' ? nearestEpdHsv : nearestEpd
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x
        const r = Math.max(0, Math.min(255, R[idx]))
        const g = Math.max(0, Math.min(255, G[idx]))
        const b = Math.max(0, Math.min(255, B[idx]))
        const best = nearest(r, g, b)
        quant[idx] = best
        const [pr, pg, pb] = PALETTE_RGB[best]
        const er = r - pr
        const eg = g - pg
        const eb = b - pb
        if (x + 1 < width) {
          R[idx + 1] += (er * 7) / 16
          G[idx + 1] += (eg * 7) / 16
          B[idx + 1] += (eb * 7) / 16
        }
        if (x > 0 && y + 1 < height) {
          R[idx + width - 1] += (er * 3) / 16
          G[idx + width - 1] += (eg * 3) / 16
          B[idx + width - 1] += (eb * 3) / 16
        }
        if (y + 1 < height) {
          R[idx + width] += (er * 5) / 16
          G[idx + width] += (eg * 5) / 16
          B[idx + width] += (eb * 5) / 16
        }
        if (x + 1 < width && y + 1 < height) {
          R[idx + width + 1] += (er * 1) / 16
          G[idx + width + 1] += (eg * 1) / 16
          B[idx + width + 1] += (eb * 1) / 16
        }
      }
    }
  }

  const raw = new Uint8Array(n / 4)
  for (let i = 0; i < n; i++) {
    const epd = quant[n - 1 - i]
    const bytePos = i >> 2
    const shift = 6 - ((i & 3) << 1)
    raw[bytePos] |= epd << shift
  }

  return packbitsEncode(raw)
}

export function imageDataToRle(imageData) {
  return processImageData(imageData)
}

export function rleToRgba(rle) {
  const raw = packbitsDecode(rle)
  const n = EPD_WIDTH * EPD_HEIGHT
  const out = new Uint8ClampedArray(n * 4)

  for (let i = 0; i < n; i++) {
    const bytePos = i >> 2
    const shift = 6 - ((i & 3) << 1)
    const epd = (raw[bytePos] >> shift) & 0x03
    const dstIdx = (n - 1 - i) * 4
    const [r, g, b] = PALETTE_RGBA[epd]
    out[dstIdx] = r
    out[dstIdx + 1] = g
    out[dstIdx + 2] = b
    out[dstIdx + 3] = 255
  }
  return out
}
